import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Package, IndianRupee, AlertTriangle } from "lucide-react";
import InventoryTabs from "@/components/inventory/InventoryTabs";
import SEO from "@/components/SEO";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function TreatmentKits() {
  const [kits, setKits] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null); // {} for new, kit doc for edit
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [k, i, c] = await Promise.all([
        api.get("/treatment-kits"),
        api.get("/inventory/items"),
        api.get("/procedure-catalog"),
      ]);
      setKits(k.data);
      setInventory(i.data);
      setCatalog(c.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load kits"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const removeKit = async (kid) => {
    if (!window.confirm("Delete this kit? Existing treatments already consumed will not be affected."))
      return;
    try {
      await api.delete(`/treatment-kits/${kid}`);
      toast.success("Kit deleted");
      loadAll();
    } catch (e) {
      toast.error(apiError(e, "Failed"));
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="treatment-kits-page">
      <InventoryTabs />
      <SEO title="Treatment Kits" noindex />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-ink">Treatment Kits</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Attach inventory items to a procedure once. Every time that treatment is marked
            <em> completed</em>, materials are automatically deducted from stock — no manual entry.
          </p>
        </div>
        <button
          data-testid="new-kit-btn"
          onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 rounded-full bg-pine text-linen px-5 py-2.5 text-sm font-semibold hover:bg-pine/90 transition">
          <Plus className="w-4 h-4" /> New kit
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : kits.length === 0 ? (
        <EmptyState onCreate={() => setEditing({})} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {kits.map((k) => (
            <KitCard key={k.id} kit={k} onEdit={() => setEditing(k)} onDelete={() => removeKit(k.id)} />
          ))}
        </div>
      )}

      {editing && (
        <KitEditor
          kit={editing}
          inventory={inventory}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="bg-white border rounded-2xl p-10 text-center" data-testid="kits-empty-state">
      <Package className="w-12 h-12 text-clay/60 mx-auto mb-4" />
      <h3 className="font-serif text-xl text-ink mb-2">No treatment kits yet</h3>
      <p className="text-sm text-slate-600 max-w-md mx-auto mb-6">
        Create a kit for each common procedure. When a doctor marks the treatment complete,
        the materials are auto-consumed from inventory.
      </p>
      <button
        data-testid="empty-state-create-kit"
        onClick={onCreate}
        className="inline-flex items-center gap-2 rounded-full bg-pine text-linen px-5 py-2.5 text-sm font-semibold">
        <Plus className="w-4 h-4" /> Create your first kit
      </button>
    </div>
  );
}

function KitCard({ kit, onEdit, onDelete }) {
  const hasMissing = kit.items.some((i) => i.missing);
  const lowStock = kit.items.filter((i) => !i.missing && i.reorder_level > 0 && i.current_stock <= i.reorder_level);
  return (
    <div
      data-testid={`kit-card-${kit.id}`}
      className="bg-white rounded-2xl border p-5 space-y-3 hover:shadow-md transition">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-serif text-lg text-ink">{kit.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {kit.procedure}{kit.variant ? ` · ${kit.variant}` : ""}
          </div>
        </div>
        <div className="flex gap-1">
          <button
            data-testid={`edit-kit-${kit.id}`}
            onClick={onEdit}
            className="p-1.5 rounded-lg hover:bg-linen/60 text-slate-500" aria-label="Edit">
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            data-testid={`delete-kit-${kit.id}`}
            onClick={onDelete}
            className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" aria-label="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-1.5 pt-1">
        {kit.items.map((it, idx) => (
          <div
            key={`${kit.id}-${it.item_id || idx}`}
            className={`flex items-center justify-between text-sm rounded-lg px-2 py-1 ${it.missing ? "bg-red-50 text-red-700" : "bg-linen/40"}`}>
            <span className="truncate">
              {it.item_name}
              {it.missing && <span className="ml-1 text-xs">(deleted)</span>}
            </span>
            <span className="text-slate-600 font-medium tabular-nums">
              {it.qty} {it.unit}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t text-sm">
        <span className="text-slate-500 flex items-center gap-1">
          <IndianRupee className="w-3.5 h-3.5" /> Est. cost
        </span>
        <span className="font-serif text-lg text-ink tabular-nums">{INR(kit.estimated_cost)}</span>
      </div>

      {(lowStock.length > 0 || hasMissing) && (
        <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {lowStock.length > 0 && `${lowStock.length} item${lowStock.length > 1 ? "s" : ""} below reorder level. `}
            {hasMissing && "One or more items were deleted — edit the kit."}
          </span>
        </div>
      )}
    </div>
  );
}

function KitEditor({ kit, inventory, catalog, onClose, onSaved }) {
  const isNew = !kit.id;
  const [name, setName] = useState(kit.name || "");
  const [procedure, setProcedure] = useState(kit.procedure || "");
  const [variant, setVariant] = useState(kit.variant || "");
  const [autoDeduct, setAutoDeduct] = useState(kit.auto_deduct !== false);
  const [isActive, setIsActive] = useState(kit.is_active !== false);
  const [items, setItems] = useState(
    kit.items?.map((i) => ({ _rid: crypto.randomUUID(), item_id: i.item_id, qty: i.qty })) || [],
  );
  const [saving, setSaving] = useState(false);

  const procedureOptions = Array.from(
    new Set(catalog.map((c) => c.name).filter(Boolean))
  ).sort();
  const variantOptions = catalog.filter((c) => c.name === procedure && c.variant).map((c) => c.variant);

  // Auto-suggest kit name from procedure + variant
  useEffect(() => {
    if (isNew && procedure && !name) {
      setName(variant ? `${procedure} · ${variant} Kit` : `${procedure} Kit`);
    }
  }, [procedure, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => setItems((s) => [...s, { _rid: crypto.randomUUID(), item_id: "", qty: 1 }]);
  const updateItem = (idx, patch) =>
    setItems((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((s) => s.filter((_, i) => i !== idx));

  const save = async () => {
    if (!name.trim()) return toast.error("Kit name is required");
    if (!procedure) return toast.error("Choose a procedure");
    const valid = items.filter((it) => it.item_id && Number(it.qty) > 0);
    if (valid.length === 0) return toast.error("Add at least one item with quantity > 0");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        procedure,
        variant: variant || null,
        auto_deduct: autoDeduct,
        is_active: isActive,
        items: valid.map((it) => ({ item_id: it.item_id, qty: Number(it.qty) })),
      };
      if (isNew) await api.post("/treatment-kits", payload);
      else await api.patch(`/treatment-kits/${kit.id}`, payload);
      toast.success(isNew ? "Kit created" : "Kit updated");
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const total = items.reduce((sum, it) => {
    const inv = inventory.find((i) => i.id === it.item_id);
    return sum + (inv ? Number(it.qty || 0) * Number(inv.unit_cost || 0) : 0);
  }, 0);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" data-testid="kit-editor">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white p-6 border-b flex items-center justify-between">
          <h2 className="font-serif text-2xl text-ink">
            {isNew ? "New Treatment Kit" : "Edit Kit"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Procedure</label>
              <input
                data-testid="kit-procedure-input"
                list="procedures-list"
                value={procedure}
                onChange={(e) => setProcedure(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                placeholder="e.g. Root Canal"
              />
              <datalist id="procedures-list">
                {procedureOptions.map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Variant (optional)</label>
              <input
                data-testid="kit-variant-input"
                list="variants-list"
                value={variant}
                onChange={(e) => setVariant(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                placeholder="e.g. Molar"
              />
              <datalist id="variants-list">
                {variantOptions.map((v) => <option key={v} value={v} />)}
              </datalist>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Kit name</label>
            <input
              data-testid="kit-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              placeholder="e.g. RCT Molar Kit"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="auto-deduct-checkbox"
              checked={autoDeduct}
              onChange={(e) => setAutoDeduct(e.target.checked)}
              className="w-4 h-4"
            />
            Automatically deduct materials on treatment completion
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="is-active-checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4"
            />
            Active — visible in the &ldquo;Kit Used Today&rdquo; picker on visit completion
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Items to consume</label>
              <button
                data-testid="add-item-btn"
                type="button"
                onClick={addItem}
                className="text-xs text-ink hover:underline inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add item
              </button>
            </div>
            <div className="space-y-2">
              {items.length === 0 && (
                <div className="text-sm text-slate-400 italic border border-dashed rounded-lg p-4 text-center">
                  No items yet. Click "Add item" to begin.
                </div>
              )}
              {items.map((it, idx) => {
                const inv = inventory.find((i) => i.id === it.item_id);
                const currentStock = inv?.stock ?? 0;
                const insufficient = inv && Number(it.qty || 0) > currentStock;
                return (
                  <div key={it._rid || idx} className="flex items-center gap-2" data-testid={`kit-item-row-${idx}`}>
                    <select
                      value={it.item_id}
                      onChange={(e) => updateItem(idx, { item_id: e.target.value })}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm">
                      <option value="">— Select material —</option>
                      {inventory.map((inv) => (
                        <option key={inv.id} value={inv.id}>
                          {inv.name} · stock: {inv.stock} {inv.unit}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      value={it.qty}
                      onChange={(e) => updateItem(idx, { qty: e.target.value })}
                      className={`w-20 px-3 py-2 border rounded-lg text-sm text-right ${insufficient ? "border-red-400 text-red-600" : ""}`}
                      placeholder="qty"
                    />
                    <span className="text-xs text-slate-500 w-14">{inv?.unit || ""}</span>
                    <button
                      onClick={() => removeItem(idx)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" aria-label="Remove">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {items.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-linen/50 rounded-lg text-sm">
              <span className="text-slate-600">Estimated material cost per use</span>
              <span className="font-serif text-lg text-ink tabular-nums">{INR(total)}</span>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white p-6 border-t flex justify-end gap-2">
          <button
            data-testid="cancel-kit-btn"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button
            data-testid="save-kit-btn"
            onClick={save}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-pine text-linen text-sm font-semibold hover:bg-pine/90 disabled:opacity-50">
            {saving ? "Saving…" : (isNew ? "Create kit" : "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
}
